import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useLocation } from 'react-router'
import { Expansible } from 'components/Expansible'
import TableLoadingAnimation from 'components/TableLoadingAnimation'
import AutomaticQuestions from 'components/ProspectDashboard/AutomaticQuestions'
import AdditionalQuestionInfo from 'components/ProspectDashboard/AdditionalQuestionInfo'
import ExpandedInfo from 'components/ExpandedInfo'

import { useSelector } from 'react-redux'

import ProspectDetails from 'components/ProspectDetails'
import { toast } from 'react-toastify'
import LoadingSplash from 'components/LoadingSplash'
import ApplicantInfo from 'components/ApplicantInfo'
import BuildDueDiligenceScoreBoard from 'components/ExpandedInfo/BuildDueDiligenceScoreBoard'
import {
  getAntiFraudDetails,
  getApplicationById,
  getApplicationOpenQuestions,
  getApplicationReviews,
  getDueDiligenceForm,
  getExtraAutomaticQuestions,
} from 'services/apiKYP'

import AdditionalQuestionForm from 'components/AdditionalQuestionForm'
import ManualAnalysisList from 'components/ManualAnalysisList'
import { AssignmentOutlined, FindInPageOutlined } from '@mui/icons-material'
import { Alert, AlertTitle, Badge, Button } from '@mui/material'
import { formQuestionHasPositiveAnswer } from 'utils/formAnswers'
import FormDisplay from 'components/DueDiligenceList/FormDisplay'
import AlertMessage from 'components/AlertMessage'
import { APPLICATION_STATUS } from 'utils/constants/kypStatuses'
import DDScoreExpansibleTitle from 'components/DDScoreExpansibleTitle'
import _ from 'lodash'
import styles from './styles'

function ApplicationDetails() {
  const location = useLocation()
  const classes = styles()
  const queryParameters = new URLSearchParams(location.search)
  const application_uuid = queryParameters.get('application_uuid')

  const token = useSelector((state) => state.auth.access_token)

  const [loading, setLoading] = useState(false)
  const [loadingAntifraude, setLoadingAntifraude] = useState(false)
  const [loadingReview, setLoadingReview] = useState(false)
  const [loadingFormData, setLoadingFormData] = useState(false)
  const [applicationData, setApplicationData] = useState({})
  const [antifraudeId, setAntifraudeId] = useState('')
  const [antifraudeData, setAntifraudeData] = useState(null)
  const [automaticQuestions, setAutomaticQuestions] = useState([])
  const [openQuestions, setOpenQuestions] = useState([])
  const [dueDiligenceFormData, setDueDiligenceFormData] = useState({})
  const [reviewsList, setReviewList] = useState([])
  const [pendingReviewsList, setPendingReviewsList] = useState([])

  const loadApplicationData = useCallback(async () => {
    setLoading(true)
    getApplicationById(token, application_uuid)
      .then((response) => {
        setAntifraudeId(response?.data?.prospect?.latest_antifraude?.request_id)
        setApplicationData(response.data)
      })
      .catch(() => {
        toast.error('Houve um erro ao obter os dados da aplicação.')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [token, application_uuid])

  const loadCandidateDetails = async () => {
    if (antifraudeId !== '') {
      setLoadingAntifraude(true)
      getAntiFraudDetails(token, antifraudeId)
        .then((response) => {
          setAntifraudeData(response.data)
        })
        .catch(() => {
          toast.error('Houve um erro ao carregar os dados de antifraude do assessor')
        })
        .finally(() => {
          setLoadingAntifraude(false)
        })
    }
  }

  const loadAutomaticExtraQuestions = () => {
    if (application_uuid !== '') {
      getExtraAutomaticQuestions(token, application_uuid)
        .then((response) => {
          setAutomaticQuestions(response.data)
        })
        .catch(() => {
          toast.error('Houve um erro ao obter os esclarecimentos adicionais')
        })
    }
  }

  const loadOpenExtraQuestions = () => {
    if (application_uuid !== '') {
      getApplicationOpenQuestions(token, application_uuid)
        .then((response) => {
          setOpenQuestions(response.data)
        })
        .catch(() => {
          toast.error('Houve um erro ao obter as perguntas adicionais')
        })
    }
  }

  const loadReviewData = () => {
    setLoadingReview(true)
    getApplicationReviews(token, application_uuid)
      .then((response) => {
        setReviewList(response.data)
        setPendingReviewsList(response.data.filter(review => review.status === 'AWAITING_DECISION'))
      })
      .catch(() => {
        toast.error()
      })
      .finally(() => {
        setLoadingReview(false)
      })
  }

  const loadDueDiligenceFormData = async () => {
    const id = applicationData?.form?.uuid
    if (id) {
      setLoadingFormData(true)
      return getDueDiligenceForm(token, id)
        .then((response) => {
          setDueDiligenceFormData(response.data)
        })
        .catch(() => {
          toast.error('Houve um Erro ao carregar os dados do formulário de Due Diligence')
        })
        .finally(() => {
          setLoadingFormData(false)
        })
    }
    return null
  }

  useEffect(() => {
    loadApplicationData()
    loadAutomaticExtraQuestions()
    loadOpenExtraQuestions()
    loadReviewData()
  }, [loadApplicationData])

  useEffect(() => {
    loadCandidateDetails()
  }, [antifraudeId])

  useEffect(() => {
    loadDueDiligenceFormData()
  }, [applicationData])

  const onSubmitQuestion = () => {
    loadOpenExtraQuestions()
  }

  const onSubmitReview = (wasReproved) => {
    if (wasReproved) {
      loadApplicationData()
    }
    loadReviewData()
  }

  const formExpansibleTitle = (data) => {
    const { titleText, occurrencesCount } = data
    return (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <Badge
          badgeContent={occurrencesCount}
          color="primary"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <AssignmentOutlined fontSize="small" style={{ marginRight: '0.4rem' }} />
        </Badge>
        {titleText}
      </span>
    )
  }

  const countDueDiligenceFormAnswersWithOccurrences = () => {
    if (!dueDiligenceFormData?.answers) {
      return 0
    }
    return dueDiligenceFormData.answers.filter((an) => formQuestionHasPositiveAnswer(an.answer_object)).length
  }

  const renderDueDiligenceFormAlert = () => {
    let text = ''
    if (['DD_IN_PROGRESS', 'PROCESSING_BGC', 'PROCESSING_MATCH'].includes(applicationData.status)) {
      text = 'A aplicação ainda não atingiu a etapa de solicitação de perguntas adicionais.'
      text +=
        applicationData?.due_diligence_score_board?.final_score == null
          ? ' O score do Due Diligence ainda não está disponível.'
          : ''
    } else {
      text = 'A aplicação não pode receber novas perguntas adicionais.'
    }
    return (
      <div style={{ marginTop: 20 }}>
        <AlertMessage text={text} />
      </div>
    )
  }

  const reviewsSectionRef = useRef(null)
  
  const handleClickGoToReviews = () => {
    reviewsSectionRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <header className={classes.header}>
          <h1>Detalhes da aplicação</h1>
          <p>
            Nesta tela você terá uma visão completa de todas as etapas da aplicação do Prospect:
            Antifraude, formulário de Due Diligence e Background Check. Poderá fazer perguntas
            adicionais ao candidato e revisar apontamentos encontrados.
          </p>
        </header>
        {loading ? (
          <TableLoadingAnimation />
        ) : (
          <div className={classes.wrapper}>
            <section className={classes.section}>
              <h2 className={classes.title}>Dados do aplicante</h2>
              <ApplicantInfo
                applicantData={applicationData?.prospect?.person}
                applicationStatus={applicationData?.status}
                applicationResult={applicationData?.result}
              />
            </section>

            { !_.isEmpty(pendingReviewsList) && <div>
              <Alert icon={<FindInPageOutlined />} severity='warning' color='error' variant='outlined'>
                <AlertTitle>Ação requerida</AlertTitle>
                
                <div style={{ marginBottom: 10 }}>
                  <b>{pendingReviewsList.length}</b> {pendingReviewsList.length === 1  ? 'análise manual pendente de decisão.' : 'análises manuais pendentes de decisão.'} 
                </div>

                <Button color='primary' sx={{ fontSize: '1.2rem' }} variant='contained'  onClick={handleClickGoToReviews}>Analisar </Button>
              </Alert>
            </div> }

            <section className={classes.section}>
              <h2 className={classes.title}>Onboarding digital - Validação antifraude</h2>
              {loadingAntifraude ? (
                <LoadingSplash loadingText="Carregando informações do antifraude. Esse processo poderá levar até 20 segundos." />
              ) : antifraudeData ? (
                <div className={classes.prospectDetailsWrapper}>
                  <ProspectDetails info={antifraudeData} />
                </div>
              ) : null}
            </section>

            <section className={classes.section}>
              <h2 className={classes.title}>
                Due Diligence, perguntas automáticas e perguntas adicionais
              </h2>
              <div className="inner-spacement">
                {applicationData?.form?.filled ? (
                  <React.Fragment>
                    {applicationData?.due_diligence_score_board?.final_score != null && (
                      <Expansible
                        title={
                          <DDScoreExpansibleTitle
                            finalScore={applicationData.due_diligence_score_board.final_score}
                          />
                        }
                      >
                        <BuildDueDiligenceScoreBoard
                          scoreBoardData={applicationData.due_diligence_score_board}
                        />
                      </Expansible>
                    )}
                    {!loadingFormData && (
                      <Expansible
                        title={formExpansibleTitle({
                          titleText: 'Respostas do formulário de Due Diligence',
                          occurrencesCount: countDueDiligenceFormAnswersWithOccurrences(),
                        })}
                      >
                        <FormDisplay formData={dueDiligenceFormData} showPersonInfo={false} />
                      </Expansible>
                    )}
                    <Expansible
                      title={formExpansibleTitle({
                        titleText: 'Respostas das perguntas automáticas',
                        occurrencesCount: automaticQuestions?.length || 0,
                      })}
                    >
                      <AutomaticQuestions questions={automaticQuestions} />
                    </Expansible>
                    <Expansible
                      title={formExpansibleTitle({
                        titleText: 'Respostas das perguntas adicionais',
                        occurrencesCount: openQuestions?.length || 0,
                      })}
                    >
                      <AdditionalQuestionInfo
                        questions={openQuestions}
                        reloadData={loadOpenExtraQuestions}
                      />
                    </Expansible>

                    {applicationData.status !== APPLICATION_STATUS.WAITING_REVIEW ? (
                      renderDueDiligenceFormAlert()
                    ) : (
                      <AdditionalQuestionForm
                        applicationStatus={applicationData.status}
                        applicationUuid={application_uuid || ''}
                        onSubmitQuestion={onSubmitQuestion}
                      />
                    )}
                  </React.Fragment>
                ) : (
                  <AlertMessage text="O assessor ainda não enviou as respostas do formulário." />
                )}
              </div>
            </section>

            <section className={classes.section}>
              <ExpandedInfo applicationData={applicationData} />
            </section>

            {!loadingReview && reviewsList?.length > 0 ? (
              <section ref={reviewsSectionRef} className={classes.section}>
                <h2 className={classes.title}>Análises manuais</h2>
                <ManualAnalysisList issuesList={reviewsList} onSubmitReview={onSubmitReview} />
              </section>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default ApplicationDetails
